// Lightweight API client with auto auth header + refresh retry

export const API_BASE_URL = "/api/v1";
export const API_ORIGIN = "";
export function toApiAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = String(pathOrUrl);
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${API_ORIGIN}${normalized}`;
}

export function getAccessToken() {
  return localStorage.getItem("accessToken") || null;
}

export function getRefreshToken() {
  return localStorage.getItem("refreshToken") || null;
}

export function setTokens(tokens) {
  if (!tokens) return;
  if (tokens.accessToken)
    localStorage.setItem("accessToken", tokens.accessToken);
  if (tokens.refreshToken)
    localStorage.setItem("refreshToken", tokens.refreshToken);
}

export function clearAuth() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("isLoggedIn");
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  const res = await fetch(`${API_BASE_URL}/auth/token/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  const newTokens = data?.tokens;
  if (newTokens?.accessToken) setTokens(newTokens);
  return newTokens?.accessToken || null;
}

export async function apiFetch(path, options = {}) {
  const { isJson = true, retryOn401 = true, ...rest } = options;
  const headers = new Headers(rest.headers || {});
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (
    isJson &&
    !headers.has("Content-Type") &&
    rest.body &&
    !(rest.body instanceof FormData)
  ) {
    headers.set("Content-Type", "application/json");
  }
  const requestInit = { ...rest, headers };
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;

  let res = await fetch(url, requestInit);
  
  // Check for session expiration before attempting refresh
  if (res.status === 401) {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const errorData = await res.clone().json();
      if (errorData?.code === "SESSION_EXPIRED") {
        // Don't retry, handle session expiration
        clearAuth();
        const error = new Error("SESSION_EXPIRED");
        error.status = 401;
        error.data = errorData;
        throw error;
      }
    }
  }
  
  if (res.status === 401 && retryOn401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.set("Authorization", `Bearer ${newToken}`);
      res = await fetch(url, { ...rest, headers });
    }
  }

  // Attempt to parse JSON if possible
  let data = null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    // Handle session expiration specially
    if (res.status === 401 && data?.code === "SESSION_EXPIRED") {
      clearAuth();
      const error = new Error("SESSION_EXPIRED");
      error.status = res.status;
      error.data = data;
      throw error;
    }
    
    const error = new Error("API_ERROR");
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

/**
 * Fetch a public provider profile by ID or slug
 * @param {string} idOrSlug - The provider's MongoDB _id or slugified company name
 * @returns {Promise<object>} Provider profile data
 */
export async function fetchPublicProviderProfile(idOrSlug) {
  if (!idOrSlug) throw new Error("Missing provider ID or slug");

  try {
    const data = await apiFetch(`/providers/${encodeURIComponent(idOrSlug)}`, {
      method: "GET",
    });
    return data.providerProfile; // return only the profile object
  } catch (err) {
    throw err;
  }
}

export const authApi = {
  async login({ email, password }) {
    return apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },
  async signup({ email, password, phoneNumber, role }) {
    return apiFetch("/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password, phoneNumber, role }),
    });
  },
  async me() {
    return apiFetch("/auth/me", { method: "GET" });
  },
  async logout() {
    try {
      return apiFetch("/auth/logout", { method: "POST" });
    } catch (err) {
      // Even if logout API fails, clear local auth
      return { message: "Logged out" };
    }
  },
  async providerRegister(formData) {
    // formData: email, password, companyName, phoneNumber, optional documents[]
    return apiFetch("/auth/provider/register", {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },
  async providerPreUpload(formData) {
    // formData: documents[]
    return apiFetch("/auth/provider/upload", {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },
};

export const catalogApi = {
  async listCategories() {
    return apiFetch("/categories", { method: "GET" });
  },
  async listSubcategories(categoryId) {
    return apiFetch(`/categories/${categoryId}/subcategories`, {
      method: "GET",
    });
  },
  async publicSearch(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/listings?${qs}`, { method: "GET" });
  },
};

export const providerApi = {
  async createListing(formData) {
    // formData is FormData
    return apiFetch(`/listings`, {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },
  async myListings(params) {
    const qs =
      params && Object.keys(params || {}).length
        ? `?${new URLSearchParams(params).toString()}`
        : "";
    return apiFetch(`/providers/me/listings${qs}`, { method: "GET" });
  },
  async getMyProfile() {
    return apiFetch(`/providers/me/profile`, { method: "GET" });
  },
  async updateMyProfile(formData) {
    return apiFetch(`/providers/me/profile`, {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },
};

export const channelPartnerApi = {
  // Registration
  async register(formData) {
    return apiFetch(`/channel-partner/register`, {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },
  async registerSimple(data) {
    return apiFetch(`/admin/channel-partner/create`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Profile management
  async getProfile() {
    return apiFetch(`/channel-partner/profile`, { method: "GET" });
  },
  async updateProfile(data) {
    return apiFetch(`/channel-partner/profile`, {
      method: "PUT",
      body: data,
      isJson: false,
    });
  },
  async submitProfile() {
    return apiFetch(`/channel-partner/profile/submit`, { method: "POST" });
  },
  async getStatus() {
    return apiFetch(`/channel-partner/status`, { method: "GET" });
  },
  async getDashboard() {
    return apiFetch(`/channel-partner/dashboard`, { method: "GET" });
  },

  // Promoter management
  async listPromoters(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/channel-partner/promoters?${qs}`, { method: "GET" });
  },
  async getPromoterDetails(promoterId) {
    return apiFetch(`/channel-partner/promoters/${promoterId}`, {
      method: "GET",
    });
  },
  async verifyPromoter(promoterId, isVerified, notes) {
    return apiFetch(`/channel-partner/promoters/${promoterId}/verify`, {
      method: "PUT",
      body: JSON.stringify({ isVerified, notes }),
    });
  },
  async verifyPromoterPayment(promoterId, isVerified, notes) {
    return apiFetch(`/channel-partner/promoters/${promoterId}/payment`, {
      method: "PUT",
      body: JSON.stringify({ isVerified, notes }),
    });
  },
  async listProviders(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/channel-partner/providers?${qs}`, { method: "GET" });
  },

  async getNetworkStatistics() {
    return apiFetch(`/channel-partner/statistics`, { method: "GET" });
  },
};

export const promoterApi = {
  // Registration
  async register(formData) {
    return apiFetch(`/promoter/register`, {
      method: "POST",
      body: formData,
      isJson: false,
    });
  },

  // Profile management
  async getProfile() {
    return apiFetch(`/promoter/profile`, { method: "GET" });
  },
  async updateProfile(data) {
    return apiFetch(`/promoter/profile`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  async getStatus() {
    return apiFetch(`/promoter/status`, { method: "GET" });
  },
  async getDashboard() {
    return apiFetch(`/promoter/dashboard`, { method: "GET" });
  },

  // Payment submission
  async submitPayment(utrId, amount) {
    return apiFetch(`/promoter/payment`, {
      method: "POST",
      body: JSON.stringify({ utrId, amount }),
    });
  },

  // Provider management
  async createProvider(data) {
    return apiFetch(`/promoter/providers`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  async listProviders(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/promoter/providers?${qs}`, { method: "GET" });
  },
  async getProviderDetails(providerId) {
    return apiFetch(`/promoter/providers/${providerId}`, { method: "GET" });
  },
  async verifyProviderPayment(providerId, isVerified, notes) {
    return apiFetch(`/promoter/providers/${providerId}/payment`, {
      method: "PUT",
      body: JSON.stringify({ isVerified, notes }),
    });
  },
  async getNetworkStatistics() {
    return apiFetch(`/promoter/statistics`, { method: "GET" });
  },
};

export const adminApi = {
  // Dashboard
  async getDashboard() {
    return apiFetch(`/admin/dashboard`, { method: "GET" });
  },

  // Channel Partner management
  async listChannelPartners(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/admin/channel-partners?${qs}`, { method: "GET" });
  },
  async getChannelPartnerDetails(id) {
    return apiFetch(`/admin/channel-partners/${id}`, { method: "GET" });
  },
  async approveChannelPartner(id) {
    return apiFetch(`/admin/channel-partners/${id}/approve`, { method: "PUT" });
  },
  async rejectChannelPartner(id, reason) {
    return apiFetch(`/admin/channel-partners/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ rejectionReason: reason }),
    });
  },
  async updateChannelPartnerSettings(channelPartnerId, settings) {
    return apiFetch(`/admin/channel-partners/${channelPartnerId}/settings`, {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  },

  // Promoter management
  async listPromoters(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/admin/promoters?${qs}`, { method: "GET" });
  },
  async getPromoterDetails(id) {
    return apiFetch(`/admin/promoters/${id}`, { method: "GET" });
  },
  async approvePromoter(id) {
    return apiFetch(`/admin/promoters/${id}/approve`, { method: "PUT" });
  },
  async rejectPromoter(id, reason) {
    return apiFetch(`/admin/promoters/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ rejectionReason: reason }),
    });
  },

  // Provider management
  async listProviders(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/admin/providers?${qs}`, { method: "GET" });
  },
  async getProviderDetails(id) {
    return apiFetch(`/admin/providers/${id}`, { method: "GET" });
  },
  async updateProviderStatus(id, status, reason) {
    return apiFetch(`/admin/providers/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status, reason }),
    });
  },

  // Customer management
  async listCustomers(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/admin/customers?${qs}`, { method: "GET" });
  },
  async getCustomerDetails(id) {
    return apiFetch(`/admin/customers/${id}`, { method: "GET" });
  },

  // Listing management
  async listListings(params) {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/admin/listings?${qs}`, { method: "GET" });
  },
  async getListingDetails(id) {
    return apiFetch(`/admin/listings/${id}`, { method: "GET" });
  },
  async approveListing(id, notes) {
    return apiFetch(`/admin/listings/${id}/approve`, {
      method: "PUT",
      body: JSON.stringify({ notes }),
    });
  },
  async rejectListing(id, reason) {
    return apiFetch(`/admin/listings/${id}/reject`, {
      method: "PUT",
      body: JSON.stringify({ rejectionReason: reason }),
    });
  },

  // Payment verification
  async getPaymentVerificationQueue() {
    return apiFetch(`/admin/payments/verify`, { method: "GET" });
  },
  async verifyPayment(userId, isVerified, notes) {
    return apiFetch(`/admin/payments/verify`, {
      method: "PUT",
      body: JSON.stringify({ userId, isVerified, notes }),
    });
  },

  // Legacy support (for backward compatibility)
  async listUsers(params) {
    // This now returns all users based on the requested type
    const { type, role } = params || {};

    if (type === "providers" || role === "provider") {
      return this.listProviders(params);
    } else if (type === "channel-partners") {
      return this.listChannelPartners(params);
    } else if (type === "promoters") {
      return this.listPromoters(params);
    } else if (type === "customers" || role === "customer") {
      return this.listCustomers(params);
    } else {
      // Default: return all providers (for existing admin dashboard)
      return this.listProviders(params);
    }
  },

  // Category management
  async createCategory(payload) {
    return apiFetch(`/categories`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  async createSubcategory(categoryId, payload) {
    return apiFetch(`/categories/${categoryId}/subcategories`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // Update functions
  async updatePromoter(promoterId, data) {
    return apiFetch(`/admin/promoters/${promoterId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  async updateListing(listingId, data) {
    return apiFetch(`/listings/${listingId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },
  async updateProvider(providerId, data) {
    return apiFetch(`/admin/providers/${providerId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  async verifyProviderPayment(providerId, isVerified) {
    return apiFetch(`/admin/providers/${providerId}/payment/verify`, {
      method: "PUT",
      body: JSON.stringify({ isVerified }),
    });
  },
};

export const enquiryApi = {
  async submitEnquiry(data) {
    return apiFetch(`/enquiries`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

export const getAllEnquiryApi = {
  async getAll() {
    return apiFetch(`/enquiries`, {
      method: "GET",
    });
  },
};

// ✅ FIXED version of enquirystatus API
export const enquirystatus = {
  async updateStatus(id, status) {
    // ✅ Correct path for backend route: /api/v1/enquiries/:id/status
    return apiFetch(`/enquiries/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  },
};
